package com.essntl.features.file.ui.imageinfos

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.organization.domain.repository.OrganizationRepository
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class ImageInfosViewModel(
    private val fileRepository: FileRepository,
    private val proposalRepository: ProposalRepository,
    private val travelAgentRepository: TravelAgentRepository,
    private val serviceRepository: ServiceRepository,
    private val categoryRepository: CategoryRepository,
    private val organizationRepository: OrganizationRepository,
    private val toastManager: ToastManager,
    val state: MutableStateFlow<ImageInfosState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    init {
        getAllCategories()
    }

    fun onEvent(event: ImageInfosEvent) {
        when (event) {
            is ImageInfosEvent.GetImage -> getImage(event)
            is ImageInfosEvent.DeleteImage -> deleteImage()
            is ImageInfosEvent.UpdateImage -> updateImage(event)
            is ImageInfosEvent.ClearState -> clearState()
        }
    }

    private fun getImage(event: ImageInfosEvent.GetImage) =
        getData(
            getData = { fileRepository.getById(event.id) },
            getState = { state.value.getFile },
            setState = { state.value = state.value.copy(getFile = it) },
            onSuccess = { file ->
                state.update {
                    it.copy(
                        getAllServices = State(),
                        getAllProposals = State(),
                        getAllTravelAgents = State(),
                        getCountOrganizations = State(),
                    )
                }

                getServices(file.id)
                getProposals(file.id)
                getCountOrganizationsByLogo(file.id)
            },
        )

    private fun getAllCategories() =
        getData(
            getData = { categoryRepository.getAll(includeExtra = true) },
            getState = { state.value.getAllCategories },
            setState = { state.value = state.value.copy(getAllCategories = it) },
        )

    private fun getServices(id: String) =
        getData(
            getData = { serviceRepository.getServicesByImage(id) },
            getState = { state.value.getAllServices },
            setState = { state.value = state.value.copy(getAllServices = it) },
        )

    private fun getProposals(id: String) {
        getData(
            getData = { proposalRepository.getProposalsByCoverImage(id) },
            getState = { state.value.getAllProposals },
            setState = { state.value = state.value.copy(getAllProposals = it) },
        )

        getData(
            getData = { proposalRepository.getCountProposalsHistoryByCoverImage(id) },
            getState = { state.value.getCountProposalsHistory },
            setState = { state.value = state.value.copy(getCountProposalsHistory = it) },
        )
    }

    private fun getCountOrganizationsByLogo(logoId: String) =
        getData(
            getData = { organizationRepository.getCountOrganizationsByLogo(logoId) },
            getState = { state.value.getCountOrganizations },
            setState = { state.value = state.value.copy(getCountOrganizations = it) },
        )

    private fun deleteImage() {
        val image = state.value.getFile.data ?: return

        getData(
            getData = { fileRepository.delete(image) },
            getState = { state.value.deleteImage },
            setState = { state.value = state.value.copy(deleteImage = it) },
            onSuccess = {
                toastManager.success(
                    description =
                        "Image deleted successfully",
                )
            },
        )
    }

    private fun updateImage(event: ImageInfosEvent.UpdateImage) {
        val image = state.value.getFile.data ?: return

        getData(
            getData = { fileRepository.update(image.copy(keywords = event.keywords.split(","))) },
            getState = { state.value.updateImage },
            setState = { state.value = state.value.copy(updateImage = it) },
            onSuccess = { f ->
                state.update {
                    it.copy(
                        getFile = State.success(f),
                    )
                }

                toastManager.success(
                    description =
                        "Image updated successfully",
                )
            },
        )
    }

    private fun clearState() {
        state.value = ImageInfosState()
    }
}